import * as React from 'react'
import { Box, Form, Button, Typography, Loader } from 'cs-kevlar-react'
import { FormikSelect } from '../FormikSelect'
import { FormikTextField } from '../FormikTextField'
import { useFormik, FormikProps } from 'formik'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { Grid } from 'cs-kevlar-react'
import * as Yup from 'yup'
import { Option } from 'cs-kevlar-react/lib/es/Select'
import {
  DataSearchResultsViewModel,
  DocumentTypeV1,
  EmergencyResourceDataSearchResultsViewModel,
} from '../../api'
import { getDocumentTypeLabel } from 'utils'
import { usePageNotifications } from 'hooks'
import { EmergencyResource, Incident } from './dataSearchResults'
import { useDataSearchResults } from 'hooks/dataSearch'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    form: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    gridItem: {
      width: '100%',
    },
    footer: {
      width: '100%',
      display: 'flex',
      flexDirection: 'row',
    },
    actionButton: {
      marginLeft: 'auto',
      marginBottom: theme.spacing(5),
    },
    noResults: {
      textAlign: 'center',
    },
    marginTop: {
      marginTop: theme.spacing(10),
    },
  }),
)

export const DataSearchFormSchema = Yup.object().shape({
  type: Yup.string().required('The type field is required.'),
  id: Yup.string().required('The id field is required.'),
})

const documentTypeOptions: Option[] = [
  {
    label: getDocumentTypeLabel(DocumentTypeV1.EmergencyResource),
    key: getDocumentTypeLabel(DocumentTypeV1.EmergencyResource),
    value: DocumentTypeV1.EmergencyResource,
  },
  {
    label: getDocumentTypeLabel(DocumentTypeV1.Incident),
    key: getDocumentTypeLabel(DocumentTypeV1.Incident),
    value: DocumentTypeV1.Incident,
  },
]

interface SearchFormValues {
  id: string
  type: DocumentTypeV1
  // this will be used to cache bust any time they hit submit
  requestedTime: number
}

interface DataSearchComponentProps {
  results: DataSearchResultsViewModel[] | undefined
  isLoading: boolean
  classes: Record<
    'form' | 'marginTop' | 'gridItem' | 'footer' | 'actionButton' | 'noResults',
    string
  >
  formik: FormikProps<SearchFormValues>
}

export const DataSearchComponent: React.FunctionComponent<DataSearchComponentProps> = ({
  results,
  isLoading,
  classes,
  formik,
}) => (
  <Box>
    <Form onSubmit={formik.handleSubmit} className={classes.form}>
      <Grid
        container
        direction="column"
        alignItems="flex-start"
        justifyContent="center"
      >
        <Grid item className={classes.gridItem}>
          <FormikSelect
            id="DataSearch::DocumentType"
            formikProps={formik}
            label="Type"
            fieldName="type"
            fullWidth
            options={documentTypeOptions}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item className={classes.gridItem}>
          <FormikTextField
            formikProps={formik}
            fieldName="id"
            label="Incident Number / Unit ID"
            fullWidth
            autoComplete="off"
          />
        </Grid>
        <Grid item className={classes.footer}>
          <Button
            color="primary"
            type="submit"
            className={classes.actionButton}
          >
            Search
          </Button>
        </Grid>
      </Grid>
      {isLoading && <Loader size="sm" text="Loading Results" />}
      {results !== undefined && (
        <Grid container data-test="DataSearchResults">
          {results.length === 0 && (
            <Grid item xs={12} className={classes.noResults}>
              <Typography>There are no results for your search.</Typography>
            </Grid>
          )}
          {results.map((result) => {
            if (formik.values.type === DocumentTypeV1.Incident) {
              return <Incident result={result} key={result.id} />
            }

            if (formik.values.type === DocumentTypeV1.EmergencyResource) {
              return (
                <EmergencyResource
                  result={result as EmergencyResourceDataSearchResultsViewModel}
                  key={result.id}
                />
              )
            }

            return <Typography>Unknown type</Typography>
          })}
        </Grid>
      )}
    </Form>
  </Box>
)

interface DataSearchProps {
  customerAgencyId: string | null
}

export const DataSearch: React.FunctionComponent<DataSearchProps> = ({
  customerAgencyId,
}) => {
  if (customerAgencyId === null) return null

  const classes = useStyles({})

  const { dispatch: dispatchNotification } = usePageNotifications()

  const [searchFormValues, setSearchFormValues] = React.useState<
    SearchFormValues
  >({
    id: '',
    type: DocumentTypeV1.Incident,
    requestedTime: Date.now(),
  })

  const { results, isLoading, isError } = useDataSearchResults(
    customerAgencyId,
    searchFormValues.id,
    searchFormValues.type,
    searchFormValues.requestedTime,
  )

  React.useEffect(() => {
    if (isError) {
      dispatchNotification({
        type: 'SetError',
        payload: 'There was an error performing the search',
      })
    }
  }, [!!isError])

  const onSubmit = React.useMemo(
    () => (values: SearchFormValues) => {
      setSearchFormValues({
        id: values.id,
        type: values.type,
        requestedTime: Date.now(),
      })
    },
    [],
  )

  const formik = useFormik({
    initialValues: searchFormValues,
    onSubmit,
    validationSchema: DataSearchFormSchema,
  })

  return (
    <DataSearchComponent
      formik={formik}
      results={results}
      classes={classes}
      isLoading={isLoading}
    />
  )
}
